import type { AlignmentRulingValue } from './api';

/**
 * Pure "encouragement architecture" math for the alignment-review deck (the
 * owner's directive, verbatim: "I also need some sort of encouragement to
 * work through them slowly... show them why it's important... then show them
 * the results"). Kept framework-free (no React) so every number the deck
 * shows a user is directly unit-pinned, never eyeballed in JSX — the NO
 * INVENTION law applies to encouragement copy exactly like it applies to a
 * résumé bullet: every count here traces to a REAL ruling this session or a
 * REAL remaining-flags length, never a fabricated milestone.
 *
 * THE COUNTING RULE: "settled" is SESSION-LOCAL (the brief: "derived from
 * ACTUAL rulings this session + remaining flags... Rulings are already
 * terminal server-side"). This module never claims to know how many flags
 * existed before this session started, or how many a user settled in a PRIOR
 * session — it only ever reports what happened in front of the user just
 * now, plus what is honestly left in the currently-served list. A page
 * refresh resets this to zero-settled/all-remaining, which is the honest
 * behavior (no persistence promised for v1 — see this lane's own brief).
 */

/** One completed ruling, tracked the instant the deck forwards a card's
 *  terminal action upward — the Deck already knows exactly which of its four
 *  callbacks fired for which achievementId (it owns the forwarding), so this
 *  is real, grounded, already-available data, not a re-derivation. */
export interface SettledRuling {
  achievementId: string;
  ruling: AlignmentRulingValue;
}

export interface AlignmentReviewProgress {
  /** Real count of rulings this session actually completed (any of the four
   *  terminal values). */
  settledCount: number;
  /** The CURRENT remaining flags length — real, server-truthful (a ruling
   *  ledger row already excludes a ruled achievement from the next fetch;
   *  this is never a locally-decremented guess). */
  remainingCount: number;
  /** settledCount + remainingCount — the honest denominator for "X settled,
   *  Y to go" framing. NOT a fixed "total flags ever" figure (this session
   *  may have started mid-way through a prior session's work); it is simply
   *  "everything currently in view, settled or not," which is the only total
   *  this module can state without inventing history it doesn't have. */
  totalInView: number;
  /** True once remainingCount reaches 0 AND at least one ruling has actually
   *  happened this session (a user who opens the deck to an already-empty
   *  list — the 'resting' display state — has settled nothing; that is NOT
   *  "all settled," it's "nothing here yet," a materially different, equally
   *  honest state the deck's existing resting branch already owns). */
  allSettledThisSession: boolean;
}

export function computeAlignmentReviewProgress(
  settled: SettledRuling[],
  remainingCount: number,
): AlignmentReviewProgress {
  const settledCount = settled.length;
  return {
    settledCount,
    remainingCount,
    totalInView: settledCount + remainingCount,
    allSettledThisSession: settledCount > 0 && remainingCount === 0,
  };
}

/** The header progress line's exact copy — "X settled · Y to go". Renders
 *  only when there is a real total to speak of (totalInView > 0); the caller
 *  is responsible for not rendering this at all when totalInView is 0 (the
 *  'resting' state already has its own honest copy for a genuinely empty
 *  deck — this line is never shown standing in for that state). */
export function progressLineCopy(progress: AlignmentReviewProgress): string {
  return `${progress.settledCount} settled · ${progress.remainingCount} to go`;
}

/** The progress bar's fill percentage, 0-100, clamped. 0/0 (nothing settled,
 *  nothing remaining — should never reach the caller per progressLineCopy's
 *  own guard, but kept safe rather than NaN) resolves to 0, not 100 — an
 *  empty bar is the honest default over a fabricated "full" one. */
export function progressPercent(progress: AlignmentReviewProgress): number {
  if (progress.totalInView <= 0) return 0;
  const pct = (progress.settledCount / progress.totalInView) * 100;
  return Math.max(0, Math.min(100, pct));
}

/** Tally settled rulings by kind — the exact breakdown the completion
 *  moment's copy needs ("N stories merged into M clean tellings, K confirmed
 *  where they were"). Every number here is a real count of THIS session's
 *  rulings, grouped by the ruling value the user actually picked — never
 *  inferred or estimated. */
export interface SettledTally {
  moved: number;
  kept: number;
  flaggedSplit: number;
  merged: number;
}

export function tallySettledRulings(settled: SettledRuling[]): SettledTally {
  const tally: SettledTally = { moved: 0, kept: 0, flaggedSplit: 0, merged: 0 };
  for (const s of settled) {
    if (s.ruling === 'moved') tally.moved += 1;
    else if (s.ruling === 'kept') tally.kept += 1;
    else if (s.ruling === 'flagged_split') tally.flaggedSplit += 1;
    else if (s.ruling === 'merged') tally.merged += 1;
  }
  return tally;
}

/**
 * The full completion-moment copy (the "results" payoff the owner named).
 * Composed ONLY from real tallied counts, and — the NO INVENTION discipline
 * applied to this copy specifically — each ruling kind is described as what
 * it ACTUALLY is, never smoothed into a single bucket that overstates it:
 *
 *   - 'merged'        -> collapses INTO a survivor (part of an existing
 *                        telling, never "a new clean telling," since nothing
 *                        new was created).
 *   - 'moved'/'kept'  -> genuinely CONFIRMED in a final home (a new one, or
 *                        the current one) — these two, and only these two,
 *                        are truthfully "settled for good."
 *   - 'flagged_split' -> NOT a final home — the user deferred it for a later
 *                        manual split. Bundling this into "confirmed" would
 *                        overstate what happened, so it gets its own honest
 *                        clause ("flagged for a closer look") rather than
 *                        being folded into the confirmed count.
 *
 * A session with zero of a given kind never mentions that kind — the
 * sentence only ever names what actually happened.
 */
export function completionSummaryCopy(tally: SettledTally): string {
  const confirmedCount = tally.moved + tally.kept;
  const parts: string[] = [];
  if (tally.merged > 0) {
    parts.push(`${tally.merged} ${tally.merged === 1 ? 'story' : 'stories'} merged into a cleaner telling`);
  }
  if (confirmedCount > 0) {
    parts.push(`${confirmedCount} confirmed where ${confirmedCount === 1 ? 'it belongs' : 'they belong'}`);
  }
  if (tally.flaggedSplit > 0) {
    parts.push(
      `${tally.flaggedSplit} flagged for a closer look ${tally.flaggedSplit === 1 ? 'later' : 'down the line'}`,
    );
  }
  if (parts.length === 0) return 'All settled.';
  return `All settled — ${parts.join(', ')}. Everything I write for you now starts from this.`;
}
